import { useRef, useState } from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { NvAccessDialog } from "@/components/navigator/nv-dialog";
import { getApiError } from "@/lib/api-error";
import { accessReasonOf } from "@/lib/access";
import {
  nvAgentDetailPath,
  nvLoginPath,
} from "@/components/navigator/nv-paths";
import { setMark } from "@/services/navigator/finder-service";
import { assistantApi } from "@/services/assistant-service";
import { useToast } from "@/hooks/use-toast";
import { AssistantWorkspace } from "./components/assistant-workspace";
import { SatisfactionDialog } from "./components/consultation-dialogs";
import { useAssistant } from "./hooks/use-assistant";
import type { AssistantAgent } from "./components/assistant-results";
import { insightFields } from "./insight-fields";

export { STORAGE_KEY } from "./hooks/use-consultation";

export const ChatPage = () => {
  const { user, accessLevel, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const reason = accessReasonOf(accessLevel);
  if (isLoading) return null;
  if (reason)
    return (
      <NvAccessDialog
        open
        reason={reason}
        onOpenChange={() => navigate("/", { replace: true })}
        onLogin={() =>
          navigate(nvLoginPath(location.pathname + location.search))
        }
        onSignup={() => navigate("/signup")}
      />
    );
  return <ChatSessionPage key={user?.id} />;
};

function ChatSessionPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const chat = useAssistant();
  const s = chat.session;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !!s?.feedback_due && currentLocation.pathname !== nextLocation.pathname,
  );
  const [bookmarkPending, setBookmarkPending] = useState<string | null>(null);
  const bookmarkBusy = useRef(false);
  const insight = s?.state.insight;
  const results = s?.state.results;
  const requiredComplete = s?.state.required_complete === true;
  const fields = insightFields(insight, s?.state.interests);
  const draft = [
    ...fields.map(({ label, value }) => `${label}: ${value}`),
    ...(insight?.requirements ?? []).map(
      (r) => `${r.mandatory ? "필수" : "선호"} 조건: ${r.quote}`,
    ),
  ].join("\n");
  const contact = (agent?: AssistantAgent) => {
    const params = new URLSearchParams({ source: "ai_assistant" });
    if (s) params.set("assistant_session_id", s.id);
    if (agent) params.set("agent_id", agent.id);
    navigate(`/contact?${params}`, {
      state: {
        from: s ? `/chat?session=${s.id}` : "/chat",
        assistantDraft: draft,
      },
    });
  };
  const bookmark = async (agent: AssistantAgent) => {
    if (bookmarkBusy.current) return;
    bookmarkBusy.current = true;
    setBookmarkPending(agent.id);
    try {
      const mark = await setMark("bookmarks", agent.id, !agent.bookmarked);
      if (mark.active)
        toast({
          title: "북마크에 저장했어요.",
          description: "도입 상담이 필요하면 Contact PwC를 이용해 주세요.",
        });
      chat.setSession((current) => {
        if (!current?.state.results) return current;
        const update = (items: AssistantAgent[]) =>
          items.map((a) =>
            a.id === agent.id ? { ...a, bookmarked: mark.active } : a,
          );
        return {
          ...current,
          state: {
            ...current.state,
            results: {
              ...current.state.results,
              suitable: update(current.state.results.suitable),
              exploratory: update(current.state.results.exploratory),
            },
          },
        };
      });
    } catch {
      toast({
        title: "북마크를 변경하지 못했습니다. 다시 시도해 주세요.",
        variant: "destructive",
      });
    } finally {
      bookmarkBusy.current = false;
      setBookmarkPending(null);
    }
  };
  return (
    <>
      <AssistantWorkspace
        sessionId={chat.composerKey}
        activeSessionId={s?.id}
        draftVersion={chat.draftVersion}
        messages={s?.messages ?? []}
        isLoading={chat.loading}
        disabled={!!s?.state.access_changed}
        error={
          s?.state.access_changed
            ? "자료의 공개 범위가 변경되었습니다. 새 상담을 시작해 주세요."
            : chat.error
        }
        onSend={chat.send}
        onRetry={chat.retry}
        onNew={chat.reset}
        history={chat.history}
        onRestore={chat.restore}
        onDelete={(id) => void chat.hide(id)}
        historyError={chat.historyError}
        historyMonths={chat.historyMonths}
        inputMaxBytes={chat.inputMaxBytes}
        canRetry={chat.canRetry && !s?.state.access_changed}
        onHistoryRetry={() => void chat.refreshHistory()}
        hasConversation={!!s?.messages.length}
        showResults
        insight={
          insight || fields.length
            ? {
                needs: "",
                bottleneck: "",
                environment: "",
                scope: insight?.scope ?? "",
                fields: insightFields(insight, s?.state.interests, true),
              }
            : undefined
        }
        agents={
          requiredComplete && results?.suitable.length
            ? results.suitable
            : (results?.exploratory ?? [])
        }
        requiredComplete={requiredComplete}
        exploratory={!requiredComplete || !results?.suitable.length}
        resultStatus={results?.status}
        degraded={results?.degraded}
        resultVersion={s?.revision}
        searching={chat.searching}
        onRetrySearch={
          !chat.loading && !s?.state.access_changed
            ? () =>
                void chat.send(
                  "앞서 말씀드린 업무와 조건으로 에이전트를 다시 찾아주세요.",
                )
            : undefined
        }
        demo={s?.state.demo}
        bookmarkPending={bookmarkPending}
        onContact={() => contact()}
        onInquire={contact}
        onBookmark={(agent) => void bookmark(agent)}
        onViewAgent={(agent) => navigate(nvAgentDetailPath(agent.id))}
        onViewDefinition={(agent) => navigate(`/agents/${agent.id}/definition`)}
      />
      <SatisfactionDialog
        key={`${s?.id}:${s?.revision}`}
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open && blocker.state === "blocked") blocker.proceed();
        }}
        onSubmit={async (feedback) => {
          if (!s) return;
          try {
            await assistantApi.feedback(s.id, s.revision, feedback);
          } catch (error) {
            if (getApiError(error)?.code === "STALE_REVISION") {
              const latest = await assistantApi.get(s.id);
              chat.setSession(latest);
              if (blocker.state === "blocked") blocker.reset();
              toast({
                title: "상담 결과가 변경되어 최신 내용을 불러왔어요.",
                description: "변경된 내용을 확인한 후 다시 평가해 주세요.",
              });
            }
            throw error;
          }
          toast({
            title: "평가가 제출되었습니다. 의견을 남겨 주셔서 감사합니다.",
          });
        }}
      />
    </>
  );
}
